/**
 * Provides functions for updating the handicaps for market selections
 *
 * @returns Object Set of functions to update the handicaps for market selections
 *
 * @module app/components/HandicapProvider
 */
import _ from 'lodash';
import jQuery from 'jquery';
import SportsDataRepository from './SportsDataRepository';

/**
 * Returns the handicapValue sign based on the positive or negative value
 *
 * @param {float} handicapValue
 *
 * @returns {String} Positive or negative sign
 *
 * @private
 */
var getAvalue = function getAvalue(handicapValue) {

    return ((handicapValue > 0) ? '-' : '+');
};

/**
 * Returns the handicapValue sign based on the opposite value from the positive or negative value
 *
 * @param {float} handicapValue
 *
 * @returns {String} Positive or negative sign
 *
 * @private
 */
var getDefaultValue = function getDefaultValue(handicapValue) {

    return ((handicapValue > 0) ? '+' : '-');
};

/**
 * This function will determine if a given selection is eligible to change its name based on handicap values
 *
 * @param {Object} selection Selection object in pds format
 * @param {float} handicapValue Market's handicap value
 *
 * @returns {Boolean} is handicap eligible?
 *
 * @private
 */
var isHandicapEligible = function isHandicapEligible(selection, handicapValue) {

    return (selection.hasOwnProperty('fbResult') && handicapValue !== 0 && handicapValue !== null);
};

/**
 * This function will return the appropriate sign for a handicap value
 *
 * @param {Object} selection Selection object in pds format
 * @param {float} handicapValue Market's handicap value
 *
 * @returns {String} The sign
 *
 * @private
 */
var getFBResultSign = function getFBResultSign(selection, handicapValue) {

    return (selection.fbResult === 'A') ?
        getAvalue(handicapValue) :
        getDefaultValue(handicapValue);
};

/**
 * In case of market sort HL this function will return the appropriate name for a selection in handicap
 *
 * @param {Object} selection Selection object in pds format
 * @param {float} handicapValue Market's handicap value
 * @param {String} marketSort Sort code established in the selection's market
 *
 * @returns {String} Handicap name for HL sort
 *
 * @private
 */
var getHandicapHLName = function getHandicapHLName(selection, handicapValue) {
    return handicapValue;
};

/**
 * get sign according input value
 *
 * @param {integer} value: Asian handicap value
 * @returns '+' when value is positive
 */
var getAHSign = function getAHSign(value) {
    return value > 0 ? '+' : '';
};

/**
 * Format input value to string with sign
 *
 * @param {integer} value: Asian handicap value
 * @returns {String} add sign with fixed-point notation
 */
var formatAHValue = function formatAHValue(value) {
    return getAHSign(value) + (value / 4).toFixed(1);
};

/**
 * Format the odd value to Asian Handicap String
 *
 * @param {integer} value: Asian handicapvalue
 * @returns {String} add sign with fixed-point notation
 */
var formatAHOddValue = function formatAHOddValue(value) {
    var sortedNums = value > 0 ? [value - 1, value + 1] : [value + 1, value - 1];
    return sortedNums.map(formatAHValue).join(', ');
};

/**
 * Transfer asian handicap value to formatted string
 *
 * @param {Integer} hAValue : Asian handicap value
 * @returns {string} Formatted string
 */
var asianHandicapValueToText = function (hAValue) {
    return (hAValue % 2 === 0 ? formatAHValue(hAValue) : formatAHOddValue(hAValue));
};
/**
 * Transfer asian handicap value to formatted string according fbResult
 *
 * @param {Object} selection Selection object in pds format
 * @param {Integer} handicapValue Market's handicap value
 * @returns {string} formatted asian handicap string
 */
var getAsianHandicapFormattedString = function getAsianHandicapFormattedString(selection, handicapValue) {
    var hAValue = (selection.fbResult === 'A') ? -handicapValue : +handicapValue;
    return '(' + asianHandicapValueToText(hAValue) + ')';
};

/**
 * Get Asian Goal Line FormatedString
 *
 * @param {Object} selection
 * @param {Float} handicapValue
 * @returns {String} Formated string
 */
var getHandicapGoalLineName = function getHandicapGoalLineName(selection, handicapValue) {
    return '(' + (handicapValue / 4) + ')';
};

/**
 * Transfer handicapValue to formatted string with sign used by Match Handicap and Win Handicap
 *
 * @param {Object} selection Selection object in pds format
 * @param {Integer} handicapValue Market's handicap value
 * @returns {string} formatted handicap string
 */
var getValueWithSign = function getValueWithSign(selection, handicapValue) {
    return getFBResultSign(selection, handicapValue) + Math.abs(handicapValue);
};

/**
 * Get formatting function using marketSort
 *
 * @param {string} marketSort
 * @returns {function} returns formatting method
 */
var mapMarketToHandicapFn = function mapMarketToHandicapFn(marketSort) {
    return ({
        'WH': getValueWithSign,
        'MH': getValueWithSign,
        'AH': getAsianHandicapFormattedString,
        'hl': getHandicapGoalLineName,
        'HL': getHandicapHLName,
    }[marketSort]);
};

/**
 * This function composes the appropiated name of theselectionin case of handicap
 *
 * @param {Object} selection Selection object in pds format
 *
 * @param {Float} handicapValueMarket's handicap value
 *
 * @param {String} marketSortSort code stablished in the selection's market
 *
 * @public
 */
var composeHandicapName = function composeHandicapName(selection, handicapValue, marketSort) {
    var handicapFn = mapMarketToHandicapFn(marketSort);
    return handicapFn ? handicapFn(selection, handicapValue) : '';
};

/**
 * This function returns the appropriate name of the selection in case of handicap
 *
 * @param {Object} selection Selection object in pds format
 * @param {float} handicapValue Market's handicap value
 * @param {String} marketSort Sort code established in the selection's market
 *
 * @returns {String} Handicap name
 *
 * @private
 */
var getHandicapName = function getHandicapName(selection, handicapValue, marketSort) {

    if (isHandicapEligible(selection, handicapValue)) {

        return composeHandicapName(selection, handicapValue, marketSort);
    } else {

        return '';
    }
};

/**
 * This function return the appropriate name of the selection in case of handicap
 *
 * @param {String} handicapName The handicap value prefix with result sign (+ or -)
 *
 * @returns {String} formatted handicap name
 *
 * @private
 */
var formatHandicapName = function formatHandicapName(handicapName) {

    if (handicapName) {

        return '(' + handicapName + ')';
    } else {

        return '';
    }
};

/**
 * Methods to be public
 *
 * @type {Object}
 */
var api = {

    /**
     * This function will update the handicap values from the selections of a market.
     *
     * @param market The marked that has to be updated
     * @param hcap The new value of the handicap
     *
     * @returns {void}
     *
     * @public
     */
    updateHandicap: function updateHandicap(market, hcap) {

        /**
         * This function will process a selection in order to update the handicap
         *
         * @param {Object} selection The selection update
         *
         * @returns {void}
         *
         * @private
         */
        var processSelection = function processSelection(selection) {

            //for each selection pick the dom element (handicap section), (might be multiple times on the same page)
            var selectionHcap = jQuery('[data-entityid="' + selection.id + '"]');
            var hcapText = getHandicapName(selection, hcap, market.sort);
            var hcapNode = selectionHcap.find('.selectionhandicap');

            if (hcapNode.data('is-us-event') && hcap == 0) {

                hcapNode.text('scr');
                //there are 2 market selection templates so the handicap element can be in a different place
            } else if (selectionHcap.hasClass('market__button')) {

                hcapNode.text(formatHandicapName(hcapText));
            } else {

                hcapNode.text(hcapText);
            }
        };

        /**
         * This function will process a selection group by looping through the selections
         * and calling for further processing in order to update the handicap
         *
         * @param {Object} selectionGroup The selections group to process
         *
         * @returns {void}
         *
         * @private
         */
        var processSelectionGroup = function processSelectionGroup(selectionGroup) {

            _.each(selectionGroup, processSelection);
        };

        /**
         * Get selection object from model based on key and process it
         *
         * @param {String} selectionId Selection id
         *
         * @returns {void}
         */
        var processSelectionById = function getSelectionByName(selectionId) {

            processSelection(SportsDataRepository.getModel().selections[selectionId]);
        };

        if (_.isEmpty(market.selectionGroups)) {

            _.each(market.selections, processSelectionById);
        } else {

            //iterate over the selection groups
            _.each(market.selectionGroups, processSelectionGroup);
        }

        market.$dom.attr('data-hcapValue', hcap);
    },

    /**
     * This function will populate a market selections with handicap values to be rendered on the templates
     *
     * @param market The marked that has to be rendered
     *
     * @returns {void}
     *
     * @public
     */
    setMarketSelectionsHandicap: function setMarketSelectionsHandicap(market) {

        /**
         * This function will set a selection handicap
         *
         * @param {Object} selection The selection update
         *
         * @returns {void}
         *
         * @private
         */
        var processSelection = function processSelection(selection) {

            selection.handicap = getHandicapName(selection, market.hcapValue, market.sort);
        };

        /**
         * This function will process a selection group by looping through the selections
         * and calling for further processing in order to update the handicap
         *
         * @param {Object} selectionGroup The selections group to process
         *
         * @returns {void}
         *
         * @private
         */
        var processSelectionGroup = function processSelectionGroup(selectionGroup) {

            _.each(selectionGroup, processSelection);
        };

        if (!_.isEmpty(market) && !_.isUndefined(market.hcapValue)) {

            //iterate over the selection groups
            _.each(market.selectionGroups, processSelectionGroup);
        }
    },
};

export default api;
